import type { RequestValidationEventDetail } from "../../../types/EventDetailTypes"
import { type RefObject } from "react"
import { RequestValidationTriggers, TypeOfValidations } from "../../../constants/validation-constants"
import { useEventDispatchQueue } from "../../UseEventDispatchQueue"
import { type ContainersForValidatableInputs } from "../../../helpers/getArrayOfValidatableInputsFromSource"
import { useCancelUnnecessaryValidation } from "./hooks/UseCancelUnnecessaryValidation"
import { useDispatchRequestValidationEvent } from "./hooks/UseDispatchRequestValidationEvent"

export interface UseValidationOnFieldsOptions {
    noSilentValidation: boolean
    fieldNamesWithCustomValidation?: string[]
}
/**
 * @namespace
 * @description dispatches a debounced RequestPartialValidation event upon an  
 * input trigger. Also listens for the same event in the capture phase. If the 
 * event's detail object has not been given a hasCustomValidation property,
 * it cancels events silent requests for partial validations, because the
 * standard browser behavior will be sufficient
 *
 * note: silent validation means the field will be validated, but no errors will
 * be generated. The input will only be marked as valid or invalid, so the
 * ValidationMessages styling can show or hide the messages that where generated
 * in a non-silent request.
 *
 * @fires RequestPartialValidation
 *
 * @listens RequestPartialValidation
 * @listens 'input'
 * @listens 'change'
 *
 * @param source -- the source of input elements, any validatable input element will be
 * retrieved from the source
 * @param on -- the input event that triggers this hook (for example, 'change' or 'input')
 * @param options.noSilentValidation -- disables silent validation behavior
 * @param options.fieldNamesWithCustomValidation -- one of the ways to mark fields 
 * as custom validated. Is mainly for use in useForm
 * */
export const useValidationOnFields = (
    source: NodeList | null | RefObject<ContainersForValidatableInputs | null>,
    on: RequestValidationTriggers,
    options?: UseValidationOnFieldsOptions
) => {
    const silentOnInput = !options?.noSilentValidation && on !== RequestValidationTriggers.INPUT
    const silentTrigger = silentOnInput ? RequestValidationTriggers.INPUT : RequestValidationTriggers.NEVER
    const queue = useEventDispatchQueue<RequestValidationEventDetail>()

    // dispatch events that have validation errors
    useDispatchRequestValidationEvent(source, on, TypeOfValidations.WITH_ERRORS, queue, options?.fieldNamesWithCustomValidation)

    // dispatch events that only update the validity of the input element (Silent validation)
    useDispatchRequestValidationEvent(source, silentTrigger, TypeOfValidations.SILENT, queue, options?.fieldNamesWithCustomValidation)

    // cancel silent validation events on events that have no custom validation rules
    useCancelUnnecessaryValidation(source)
}
